import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { WEBHOOK_ENDPOINTS } from '../config/api';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import LinkIcon from '@mui/icons-material/Link';

const WebhookForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const isEditMode = !!id;
  const [telegramApiKey, setTelegramApiKey] = useState("")

  const [formData, setFormData] = useState({
    name: '',
    url: '',
    type: 'Discord'
  });

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEditMode) {
      fetchWebhook();
    }
  }, [id]);

  const fetchWebhook = async () => {
    try {
      setLoading(true);
      const response = await fetch(WEBHOOK_ENDPOINTS.DETAIL(id), {
        headers: getAuthHeader(),
      });

      if (response.ok) {
        const data = await response.json();
        setFormData({
          name: data.name || '',
          url: data.url || '',
          type: data.type || 'Discord'
        });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch webhook');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleTelegramApiChange = (e) => {
    const { name, value } = e.target;
    setTelegramApiKey(value);
  };

  const handleSetTelegramUrl = async () => {
    setError(null); // Clear previous errors
    try {
      const response = await fetch(WEBHOOK_ENDPOINTS.TELEGRAM_URL(telegramApiKey), {
        headers: getAuthHeader(),
      });
  
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})); // prevent crash if no JSON
        const message = errorData?.error || `Failed to fetch URL (Status: ${response.status})`;
        setError(message);
        return;
      }
  
      const data = await response.json();
      setFormData(prev => ({
        ...prev,
        url: data?.webhookUrl || ''
      }));
  
    } catch (err) {
      console.error('Telegram URL fetch failed:', err);
      setError('Something went wrong. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const url = isEditMode ? WEBHOOK_ENDPOINTS.DETAIL(id) : WEBHOOK_ENDPOINTS.CREATE;
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        navigate('/webhooks');
      } else {
        const errorData = await response.json();
        setError(errorData.error || `Failed to ${isEditMode ? 'update' : 'create'} webhook`);
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/webhooks');
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 0, mb: 4, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3, mt: 1 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Webhooks
        </Button>
        <Typography variant="h4" component="h1" gutterBottom>
          {isEditMode ? 'Edit Webhook' : 'Add New Webhook'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, width: '100%' }}>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Webhook Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            margin="normal"
            required
            helperText="Give your webhook a descriptive name"
            sx={{mb: 4}}
          />

          <FormControl fullWidth required sx={{mb: 3}}>
            <InputLabel id="webhook-type">Webhook Type</InputLabel>
            <Select
              labelId="webhook-type"
              id="type"
              name="type"
              value={formData.type}
              label="Webhook Type"
              onChange={handleChange}
              sx={{minWidth: 200}}
            >
              <MenuItem key={1} value={"Discord"}>
                Discord
              </MenuItem>
              <MenuItem key={2} value={"Telegram"}>
                Telegram
              </MenuItem>
            </Select>
          </FormControl>

          {formData.type === "Discord" ? (
            <TextField
              fullWidth
              label="Webhook URL"
              name="url"
              value={formData.url}
              onChange={handleChange}
              margin="normal"
              required
              helperText="Ex.: https://discord.com/api/webhooks/<KEY>"
            />
          ) : (
            <>
              <TextField
                fullWidth
                label="Webhook URL (with chat_id)"
                name="url"
                value={formData.url}
                onChange={handleChange}
                margin="normal"
                required
                helperText="Ex.: https://api.telegram.org/bot<KEY>/sendMessage?chat_id=<ID>"
              />
            </>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : isEditMode ? 'Update Webhook' : 'Create Webhook'}
            </Button>
          </Box>
        </form>
      </Paper>
      

      {formData.type === "Telegram" && 
        <Paper sx={{ mt: 8, p: 3, width: '100%' }}>
          <Typography variant="h6" gutterBottom sx={{ textAlign: 'left' }}>
            If you're not sure about your Telegram chat_id, use the feature bellow:
          </Typography>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              type="button"
              variant="contained"
              color="warning"
              startIcon={<LinkIcon />}
              onClick={() => handleSetTelegramUrl()}
              disabled={!telegramApiKey}
              sx={{flex: 1, height: 56, mt: 2, mr: 4}}
            >
              Auto-fill URL
            </Button>
            <TextField
              fullWidth
              label="Telegram API Key"
              name="url"
              value={telegramApiKey}
              onChange={handleTelegramApiChange}
              margin="normal"
              helperText="Ex.: 1234567890:ASDFsafsioee8Yifdshfewp7fe"
              sx={{flex: 3}}
            />
          </Box>
        </Paper>
      }

    </Container>
  );
};

export default WebhookForm; 